import { Button, Checkbox, Input, SearchableSelect } from '@remote-medium/ui';
import { Plus, Search } from 'lucide-react';
import { useId, useState } from 'react';

import { DataTable, EmptyRow, StatusRow } from '../../../lib/ui.tsx';

interface Item {
  id: string;
  label: string;
  /** Grouping value (skill category, role family); '' when the list has none. */
  extra: string;
  /** Tag values per tag set key (role families, industry ids); empty = any. */
  tags?: Record<string, string[]>;
}

interface Option {
  value: string;
  label: string;
}

/**
 * A list of tags each item can carry: role families on skills, industries on
 * skills and role types. Only orders the pickers; ranking ignores them.
 */
export interface TagSet {
  key: string;
  /** Column heading, e.g. "For role families". */
  heading: string;
  /** Edit button, e.g. "Families". */
  button: string;
  /** Shown for an item with no tags, e.g. "Generic". */
  emptyText: string;
  options: Option[];
  /** Many options (role types): edit with a search box over a scrolling list. */
  searchable?: boolean;
  onSave: (id: string, values: string[]) => Promise<boolean>;
}

function tagsText(set: TagSet, i: Item) {
  const values = i.tags?.[set.key] ?? [];
  if (!values.length) return set.emptyText;
  return values.map((v) => set.options.find((o) => o.value === v)?.label ?? v).join(', ');
}

// Checkboxes for a short option list; a filterable scrolling list when there
// are too many to scan. Chosen options stay on top whatever the filter says.
function TagChecklist({
  set,
  values,
  onChange,
}: {
  set: TagSet;
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [q, setQ] = useState('');
  const shown = set.searchable
    ? set.options.filter(
        (o) => values.includes(o.value) || o.label.toLowerCase().includes(q.trim().toLowerCase()),
      )
    : set.options;
  const ordered = set.searchable
    ? [
        ...shown.filter((o) => values.includes(o.value)),
        ...shown.filter((o) => !values.includes(o.value)),
      ]
    : shown;
  const list = ordered.map((o) => (
    <label key={o.value} className="flex items-center gap-1.5 text-sm">
      <Checkbox
        checked={values.includes(o.value)}
        onCheckedChange={(on) =>
          onChange(on ? [...values, o.value] : values.filter((x) => x !== o.value))
        }
      />
      {o.label}
    </label>
  ));
  if (!set.searchable) return <>{list}</>;
  return (
    <div className="flex w-full min-w-64 flex-col gap-2">
      <Input
        type="search"
        aria-label={`Search ${set.button.toLowerCase()}`}
        placeholder={`Search ${set.button.toLowerCase()}`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="flex max-h-56 flex-col gap-1.5 overflow-y-auto">
        {list.length ? list : <span className="text-sm text-muted-foreground">No matches.</span>}
      </div>
      <span className="text-xs text-muted-foreground">{values.length} selected</span>
    </div>
  );
}

export function TaxonomySection({
  noun,
  items,
  loading,
  error,
  onCreate,
  onRename,
  onMerge,
  extraOptions,
  extraLabel,
  allowNewExtra,
  tagSets = [],
}: {
  /** Singular, lower case: "skill", "role type", "industry". */
  noun: string;
  items: Item[];
  loading: boolean;
  error: unknown;
  onCreate: (name: string, extra: string) => Promise<boolean>;
  onRename: (id: string, name: string) => Promise<boolean>;
  onMerge: (id: string, intoId: string) => Promise<boolean>;
  extraOptions?: Option[];
  extraLabel?: string;
  allowNewExtra?: boolean;
  tagSets?: TagSet[];
}) {
  const listId = useId();
  const [name, setName] = useState('');
  // Category / family starts empty and is required (NOT NULL for role types,
  // required by the create API for skills).
  const [extra, setExtra] = useState('');
  const canCreate = !!name.trim() && (!extraOptions || !!extra.trim());
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null);
  const [merging, setMerging] = useState<{ id: string; intoId: string | null } | null>(null);
  const [tagging, setTagging] = useState<{ id: string; key: string; values: string[] } | null>(
    null,
  );

  const extraName = (v: string) => extraOptions?.find((o) => o.value === v)?.label ?? v;
  const shown = items
    .filter(
      (i) =>
        !filter ||
        `${i.extra} ${i.label} ${tagSets.map((t) => tagsText(t, i)).join(' ')}`
          .toLowerCase()
          .includes(filter.toLowerCase()),
    )
    .sort((a, b) => `${a.extra} ${a.label}`.localeCompare(`${b.extra} ${b.label}`));
  const hasExtra = !!extraOptions;
  const ready = !loading && !error;
  const cols = 2 + (hasExtra ? 1 : 0) + tagSets.length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative lg:w-64">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            aria-label={`Filter ${noun}s`}
            placeholder={`Filter ${noun}s`}
            className="pl-8"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!canCreate) return;
            void onCreate(name.trim(), extra.trim()).then((ok) => ok && setName(''));
          }}
        >
          <Input
            aria-label={`New ${noun} name`}
            placeholder={`New ${noun}`}
            className="w-48"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {extraOptions &&
            (allowNewExtra ? (
              <>
                <Input
                  aria-label={extraLabel}
                  placeholder={extraLabel}
                  list={listId}
                  className="w-40"
                  value={extra}
                  onChange={(e) => setExtra(e.target.value)}
                />
                <datalist id={listId}>
                  {extraOptions.map((o) => (
                    <option key={o.value} value={o.value} />
                  ))}
                </datalist>
              </>
            ) : (
              <div className="w-48">
                <SearchableSelect
                  aria-label={extraLabel ?? 'Group'}
                  options={extraOptions}
                  value={extra || null}
                  onChange={(v) => setExtra(v ?? '')}
                  placeholder={extraLabel ?? 'Group'}
                />
              </div>
            ))}
          <Button type="submit" size="sm" disabled={!canCreate}>
            <Plus data-icon="inline-start" />
            Add {noun}
          </Button>
        </form>
      </div>

      <DataTable minWidth="min-w-xl">
        <thead>
          <tr>
            {hasExtra && <th className="w-56">{extraLabel}</th>}
            <th>Name</th>
            {tagSets.map((t) => (
              <th key={t.key}>{t.heading}</th>
            ))}
            <th>
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <StatusRow colSpan={cols} loading={loading} error={error} />
          {ready && shown.length === 0 && (
            <EmptyRow colSpan={cols}>
              {filter ? `No ${noun}s match “${filter}”.` : `No ${noun}s yet.`}
            </EmptyRow>
          )}
          {(ready ? shown : []).map((i) => (
            <tr key={i.id} className="hover:bg-muted/50">
              {hasExtra && (
                <td className="whitespace-nowrap text-muted-foreground">{extraName(i.extra)}</td>
              )}
              <td>
                {editing?.id === i.id ? (
                  <form
                    className="flex flex-wrap items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!editing.value.trim()) return;
                      void onRename(i.id, editing.value.trim()).then(
                        (ok) => ok && setEditing(null),
                      );
                    }}
                  >
                    <Input
                      autoFocus
                      aria-label={`Rename ${i.label}`}
                      className="w-56"
                      value={editing.value}
                      onChange={(e) => setEditing({ id: i.id, value: e.target.value })}
                    />
                    <Button size="sm" type="submit">
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() => setEditing(null)}
                    >
                      Cancel
                    </Button>
                  </form>
                ) : merging?.id === i.id ? (
                  <form
                    className="flex flex-wrap items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (merging.intoId)
                        void onMerge(i.id, merging.intoId).then((ok) => ok && setMerging(null));
                    }}
                  >
                    <span className="text-sm">
                      Merge <span className="font-semibold">{i.label}</span> into
                    </span>
                    <div className="w-56">
                      <SearchableSelect
                        aria-label="Merge into"
                        placeholder={`Pick a ${noun}`}
                        options={items
                          .filter((x) => x.id !== i.id)
                          .map((x) => ({
                            value: x.id,
                            label: x.label,
                            ...(x.extra ? { group: extraName(x.extra) } : {}),
                          }))}
                        value={merging.intoId}
                        onChange={(v) => setMerging({ id: i.id, intoId: v })}
                      />
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      type="submit"
                      disabled={!merging.intoId}
                    >
                      Merge and delete
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() => setMerging(null)}
                    >
                      Cancel
                    </Button>
                  </form>
                ) : (
                  <span className="font-medium">{i.label}</span>
                )}
              </td>
              {tagSets.map((set) => (
                <td key={set.key}>
                  {tagging?.id === i.id && tagging.key === set.key ? (
                    <form
                      className="flex flex-wrap items-center gap-x-3 gap-y-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void set.onSave(i.id, tagging.values).then((ok) => ok && setTagging(null));
                      }}
                    >
                      <TagChecklist
                        set={set}
                        values={tagging.values}
                        onChange={(values) => setTagging({ ...tagging, values })}
                      />
                      <Button size="sm" type="submit">
                        Save
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => setTagging(null)}
                      >
                        Cancel
                      </Button>
                    </form>
                  ) : (
                    <span className="text-muted-foreground">{tagsText(set, i)}</span>
                  )}
                </td>
              ))}
              <td className="w-56 text-right whitespace-nowrap">
                {!editing && !merging && !tagging && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing({ id: i.id, value: i.label })}
                    >
                      Rename
                    </Button>
                    {tagSets.map((set) => (
                      <Button
                        key={set.key}
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setTagging({ id: i.id, key: set.key, values: i.tags?.[set.key] ?? [] })
                        }
                      >
                        {set.button}
                      </Button>
                    ))}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setMerging({ id: i.id, intoId: null })}
                    >
                      Merge
                    </Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      {ready && (
        <p className="m-0 text-xs text-muted-foreground">
          Showing {shown.length} of {items.length}
        </p>
      )}
    </div>
  );
}
